import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter
} from '@angular/core';

import * as moment from 'moment';
import { ToastrService } from 'ngx-toastr';

import {
  ConsultationDevelopmentChildItem
} from 'src/app/entities/consultation.entity';

import {
  PediatricChild
} from 'src/app/entities/pediatric-child.entity/pediatric-child';

import { User } from 'src/app/entities/user.entity';
import { Doctor } from 'src/app/entities/user.entity/doctor.entity';

import { AuthService } from 'src/app/services/auth.service/auth.service';
import { DoctorPatientsService } from 'src/app/services/doctor-patients.service/doctor-patients.service';

import {
  PediatricChildSettingsService
} from 'src/app/services/pediatric-child-settings.service/pediatric-child-setting.service';

interface PediatricDevelopmentGroup {
  minOld: number;
  label: string;
  historyItems: ConsultationDevelopmentChildItem[];
  formItems: ConsultationDevelopmentChildItem[];
}

@Component({
  selector: 'app-consultation-development-child-form',
  templateUrl: './consultation-development-child-form.component.html',
  styleUrls: ['./consultation-development-child-form.component.css']
})
export class ConsultationDevelopmentChildFormComponent
  implements OnInit, OnChanges {

  @Input() consultation: any;
  @Input() vaccines: any[] = [];
  @Input() patient: User = new User();
  @Input() patientId!: string;
  @Input() old: number = 0;

@Input() ConsultationDevelopmentChildItems: ConsultationDevelopmentChildItem[] = [];

@Output() ConsultationDevelopmentChildItemsChange =
  new EventEmitter<ConsultationDevelopmentChildItem[]>();

  @Output()
  validation = new EventEmitter<boolean>();

  @Input()
  submitted = false;

  pediatricChildSettings: PediatricChild[] = [];

  /** Éléments des consultations précédentes. */
  patientpediatricChildItems:
    ConsultationDevelopmentChildItem[] = [];

  /** Éléments modifiables de la consultation actuelle. */
  consultationpediatricChildFormItems:
    ConsultationDevelopmentChildItem[] = [];

  developmentGroups: PediatricDevelopmentGroup[] = [];

  openGroups = new Set<number>();

  private currentConsultationItems =
    new Set<ConsultationDevelopmentChildItem>();

  loading = false;
  private initialized = false;
  private loadingRequest = false;

  get doctor(): Doctor {
    return this.authService.getCurrentUser() as Doctor;
  }

  constructor(
    private pediatricChildSettingsService:
      PediatricChildSettingsService,
    private doctorPatientsService: DoctorPatientsService,
    private toasterService: ToastrService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  /**
   * Important pour le mode update :
   * le parent peut transmettre la consultation après ngOnInit.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (!this.initialized) {
      return;
    }

    if (
      changes['patientId'] &&
      !changes['patientId'].firstChange
    ) {
      this.loadData();
      return;
    }

    if (
      changes['ConsultationDevelopmentChildItems'] &&
      !changes['ConsultationDevelopmentChildItems'].firstChange
    ) {
      this.rebuildCurrentItems();
    }
  }

  /**
   * Identifiant normalisé.
   */
  private key(id: any): string {
    return id === null || id === undefined || id === ''
      ? ''
      : String(id);
  }

  private getItemId(
    item: ConsultationDevelopmentChildItem
  ): string {
    const raw = item as any;

    return this.key(
      raw.id ?? raw.developmentChildId
    );
  }

  private getConsultationId(
    item: ConsultationDevelopmentChildItem
  ): string {
    const raw = item as any;

    return this.key(
      raw.consultation?.id ?? raw.consultationId
    );
  }

  private getCurrentConsultationId(): string {
    return this.key(this.consultation?.id);
  }

  /**
   * Chargement des paramètres et de l'historique.
   */
  loadData(): void {
    if (!this.patientId || this.loadingRequest) {
      return;
    }

    this.loading = true;
    this.loadingRequest = true;

    this.pediatricChildSettingsService.all().subscribe({
      next: settings => {
        this.pediatricChildSettings = Array.from(
          settings || [],
          item => PediatricChild.fromResponse(item)
        )
          .filter(item => item.isActivated)
          .sort((a, b) =>
            Number(a.minOld || 0) - Number(b.minOld || 0)
          );

        this.doctorPatientsService
          .childDevelopment(this.patientId)
          .subscribe({
            next: (response: any[]) => {
              const currentItems =
                (this.ConsultationDevelopmentChildItems || [])
                  .map(item => this.normalizeItem(item));

              const currentIds = new Set(
                currentItems
                  .map(item => this.getItemId(item))
                  .filter(id => !!id)
              );

              const currentSettingIds = new Set(
                currentItems
                  .map(item => this.key(item.setting?.id))
                  .filter(id => !!id)
              );

              const currentConsultationId =
                this.getCurrentConsultationId();

              const history = (response || [])
                .map(raw =>
                  this.normalizeItem(
                    ConsultationDevelopmentChildItem.fromResponse(raw),
                    raw
                  )
                )
                .filter(item => !!item.setting?.id)
                .filter(item => {
                  const historyConsultationId =
                    this.getConsultationId(item);

                  const itemId = this.getItemId(item);
                  const settingId = this.key(item.setting?.id);

                  // Exclure les enregistrements identiques.
                  if (itemId && currentIds.has(itemId)) {
                    return false;
                  }

                  // Un élément explicitement lié à la consultation
                  // actuelle ne doit pas être placé dans l'historique.
                  if (
                    currentConsultationId &&
                    historyConsultationId &&
                    historyConsultationId === currentConsultationId
                  ) {
                    return false;
                  }

                  // Si le parent a fourni un élément pour ce repère,
                  // celui-ci sera affiché dans le formulaire actuel.
                  // if (currentSettingIds.has(settingId)) {
                  //   return false;
                  // }

                  // En l'absence d'identifiants de consultation,
                  // la règle de déduplication par repère sert de secours.
                  return true;
                });

              this.patientpediatricChildItems =
                this.dedupeBySetting(history)
                  .sort(this.sortByMinOld);

              this.rebuildCurrentItems();

              this.loading = false;
              this.loadingRequest = false;
              this.initialized = true;
            },

            error: () => {
              this.loading = false;
              this.loadingRequest = false;

              this.toasterService.error(
                'Impossible de charger l’historique du développement de l’enfant.'
              );
            }
          });
      },

      error: () => {
        this.loading = false;
        this.loadingRequest = false;

        this.toasterService.error(
          'Impossible de charger les paramètres du développement de l’enfant.'
        );
      }
    });
  }

  /**
   * Reconstruit les éléments modifiables.
   * Utilisé aussi bien en création qu'en modification.
   */
 
private rebuildCurrentItems(): void {
  const currentItems =
    (this.ConsultationDevelopmentChildItems || [])
      .map(item => this.normalizeItem(item));

  // Repères déjà ACQUIS lors d'une consultation précédente
  const acquiredInHistory = new Set(
    this.patientpediatricChildItems
      .filter(item => !!item.developmentEvaluated)
      .map(item => this.key(item.setting?.id))
      .filter(id => !!id)
  );

  this.consultationpediatricChildFormItems =
    this.pediatricChildSettings
      .map(setting => {
        const settingId = this.key(setting.id);

        // Mode update : élément déjà lié à la consultation
        const existing = currentItems.find(
          item => this.key(item.setting?.id) === settingId
        );

        // Acquis dans CETTE consultation → modifiable
        if (existing && existing.developmentEvaluated) {
          existing.setting = setting;
          return existing;
        }

        // Déjà acquis avant → reste uniquement dans l'historique
        // (on ignore un ancien NOT_ACQUIRED de la consultation actuelle)
        if (acquiredInHistory.has(settingId)) {
          return null;
        }

        // Non acquis, déjà lié à la consultation → modifiable
        if (existing) {
          existing.setting = setting;
          return existing;
        }

        // Non acquis ou jamais évalué → nouvelle ligne modifiable
        return new ConsultationDevelopmentChildItem({
          setting,
          developmentEvaluated: false,
          checked: false
        });
      })
      .filter(
        (item): item is ConsultationDevelopmentChildItem =>
          item !== null
      )
      .sort(this.sortByMinOld);

  this.currentConsultationItems = new Set(
    this.consultationpediatricChildFormItems
  );

  this.buildDevelopmentGroups();
}

  /**
   * Normalisation des données API.
   */
  private normalizeItem(
    item: ConsultationDevelopmentChildItem,
    raw?: any
  ): ConsultationDevelopmentChildItem {
    const target: any = item;
    const source = raw ?? target;

    if (!target.id && source?.developmentChildId) {
      target.id = source.developmentChildId;
    }

    if (
      source?.developmentEvaluated !== undefined &&
      source?.developmentEvaluated !== null
    ) {
      target.developmentEvaluated =
        source.developmentEvaluated === true ||
        source.developmentEvaluated === 'true';
    }

    if (!target.consultationId && source?.consultationId) {
      target.consultationId = source.consultationId;
    }

    if (!target.consultation && source?.consultation) {
      target.consultation = source.consultation;
    }

    if (target.date) {
      const parsed = moment(
        target.date,
        ['DD/MM/YYYY', 'YYYY-MM-DD', moment.ISO_8601],
        true
      );

      target.date = parsed.isValid()
        ? parsed.toDate()
        : null;
    }

    return item;
  }

  /**
   * Garder un seul élément historique par repère.
   * Priorité à un élément évalué, puis au plus récent.
   */
  private dedupeBySetting(
    items: ConsultationDevelopmentChildItem[]
  ): ConsultationDevelopmentChildItem[] {
    const result =
      new Map<string, ConsultationDevelopmentChildItem>();

    const timestamp = (item: any): number => {
      const value = item.updatedAt ?? item.createdAt ?? 0;
      return new Date(value).getTime() || 0;
    };

    items.forEach(item => {
      const id = this.key(item.setting?.id);

      if (!id) {
        return;
      }

      const previous = result.get(id);

      if (
        !previous ||
        (!!item.developmentEvaluated &&
          !previous.developmentEvaluated) ||
        (
          !!item.developmentEvaluated ===
            !!previous.developmentEvaluated &&
          timestamp(item) > timestamp(previous)
        )
      ) {
        result.set(id, item);
      }
    });

    return Array.from(result.values());
  }

  private sortByMinOld = (
    a: ConsultationDevelopmentChildItem,
    b: ConsultationDevelopmentChildItem
  ): number => {
    const ageDiff =
      Number(a.setting?.minOld ?? 0) -
      Number(b.setting?.minOld ?? 0);

    if (ageDiff !== 0) {
      return ageDiff;
    }

    const orderA = Number(
      a.setting?.order ?? Number.MAX_SAFE_INTEGER
    );

    const orderB = Number(
      b.setting?.order ?? Number.MAX_SAFE_INTEGER
    );

    return orderA - orderB ||
      (a.setting?.label ?? '').localeCompare(
        b.setting?.label ?? '',
        'fr'
      );
  };

  private getGroupLabel(minOld: number): string {
    if (minOld <= 1) {
      return 'Nouveau-né (0–1 mois)';
    }

    if (minOld < 12) {
      return minOld + ' mois';
    }

    const years = Math.floor(minOld / 12);
    const months = minOld % 12;

    const label = years + (years > 1 ? ' ans' : ' an');

    return months
      ? label + ' et ' + months + ' mois'
      : label;
  }

  /**
   * Regrouper historique et consultation actuelle.
   */
  buildDevelopmentGroups(): void {
    const groups =
      new Map<number, PediatricDevelopmentGroup>();

    const getGroup = (
      item: ConsultationDevelopmentChildItem
    ): PediatricDevelopmentGroup => {
      const minOld = Number(item.setting?.minOld ?? 0);

      if (!groups.has(minOld)) {
        groups.set(minOld, {
          minOld,
          label: this.getGroupLabel(minOld),
          historyItems: [],
          formItems: []
        });
      }

      return groups.get(minOld)!;
    };
    // Un repère présent dans le formulaire actuel
    // n'est jamais répété dans l'historique (correction du doublon).
    const formSettingIds = new Set(
      this.consultationpediatricChildFormItems
        .map(item => this.key(item.setting?.id))
        .filter(id => !!id)
    );

    this.patientpediatricChildItems
      .filter(item => !formSettingIds.has(this.key(item.setting?.id)))
      .forEach(item => {
        getGroup(item).historyItems.push(item);
      });
    this.consultationpediatricChildFormItems.forEach(item => {
      getGroup(item).formItems.push(item);
    });

    this.developmentGroups = Array.from(groups.values())
      .sort((a, b) => a.minOld - b.minOld);

    if (this.openGroups.size === 0 && this.developmentGroups.length) {
      const currentGroup = [...this.developmentGroups]
        .reverse()
        .find(group => Number(this.old) >= group.minOld);

      this.openGroups.add(
        (currentGroup || this.developmentGroups[0]).minOld
      );
    }
  }

  /**
   * N'envoyer au parent que les éléments évalués
   * de la consultation actuelle.
   */
 get getConsultationDevelopmentChildItems():
  ConsultationDevelopmentChildItem[] {

  return this.consultationpediatricChildFormItems.map(item => {
    item.status = item.developmentEvaluated
      ? 'ACQUIRED'
      : "";

    return item;
  });
}

  _change(): void {
    this.ConsultationDevelopmentChildItemsChange.emit(
      this.getConsultationDevelopmentChildItems
    );
  }

  belongsToCurrentConsultation(
    item: ConsultationDevelopmentChildItem
  ): boolean {
    if (
      this.currentConsultationItems.has(item)
    ) {
      return true;
    }

    const itemConsultationId =
      this.getConsultationId(item);

    const currentConsultationId =
      this.getCurrentConsultationId();

    if (itemConsultationId && currentConsultationId) {
      return itemConsultationId === currentConsultationId;
    }

    const itemId = this.getItemId(item);

    return !!itemId &&
      (this.ConsultationDevelopmentChildItems || [])
        .some(current => this.getItemId(current) === itemId);
  }

  /**
   * Règle conservée : verrouiller si old > minOld.
   * Les deux valeurs doivent être exprimées en mois.
   */
 isItemLocked(
  item: ConsultationDevelopmentChildItem
): boolean {
  if (!this.belongsToCurrentConsultation(item)) {
    return false;
  }

  const old = Number(this.old);
  const minOld = Number(item.setting?.minOld ?? 0);

  return Number.isFinite(old) &&
    Number.isFinite(minOld) &&
    old > minOld;
}

isItemDisabled(
  item: ConsultationDevelopmentChildItem
): boolean {
  // Bloquer uniquement les éléments qui ne font pas
  // partie de la consultation actuelle.
  return !this.belongsToCurrentConsultation(item);
}

onItemChange(item: ConsultationDevelopmentChildItem): void {
  const items = [...this.consultationpediatricChildFormItems];

  const index = items.findIndex(existing => {
    const existingSettingId = this.key(existing.setting?.id);
    const itemSettingId = this.key(item.setting?.id);

    if (existingSettingId && itemSettingId) {
      return existingSettingId === itemSettingId;
    }

    const existingId = this.getItemId(existing);
    const itemId = this.getItemId(item);

    return !!itemId && existingId === itemId;
  });

  if (index !== -1) {
    items[index] = item;
  }

  this.consultationpediatricChildFormItems = items;

  this.ConsultationDevelopmentChildItemsChange.emit(
    items.filter(existing => !!existing.developmentEvaluated)
  );
}


  getDate(
    item: ConsultationDevelopmentChildItem
  ): Date {
    if (!item.date) {
      return moment().startOf('day').toDate();
    }

    const parsed = moment(
      item.date,
      ['DD/MM/YYYY', 'YYYY-MM-DD', moment.ISO_8601],
      true
    );

    return parsed.isValid()
      ? parsed.toDate()
      : moment().startOf('day').toDate();
  }

  getInputDate(
    item: ConsultationDevelopmentChildItem
  ): string {
    return moment(this.getDate(item)).format('YYYY-MM-DD');
  }

  getDisplayDate(
    item: ConsultationDevelopmentChildItem
  ): string {
    return item.date
      ? moment(this.getDate(item)).format('DD/MM/YYYY')
      : '—';
  }

  onDateChange(
    item: ConsultationDevelopmentChildItem,
    date: Date | string | null
  ): void {
    if (this.isItemDisabled(item)) {
      return;
    }

    if (!date) {
      item.date = null as any;
    } else if (date instanceof Date) {
      item.date = date;
    } else {
      const parsed = moment(
        date,
        ['YYYY-MM-DD', 'DD/MM/YYYY', moment.ISO_8601],
        true
      );

      item.date = parsed.isValid()
        ? parsed.toDate()
        : null as any;
    }

    this._change();
  }

  isOpen(minOld: number): boolean {
    return this.openGroups.has(minOld);
  }

  toggleGroup(minOld: number): void {
    if (this.openGroups.has(minOld)) {
      this.openGroups.delete(minOld);
    } else {
      this.openGroups.add(minOld);
    }
  }

  expandAll(): void {
    this.developmentGroups.forEach(
      group => this.openGroups.add(group.minOld)
    );
  }

  collapseAll(): void {
    this.openGroups.clear();
  }

  getCheckedCount(
    group: PediatricDevelopmentGroup
  ): number {
    return [
      ...group.historyItems,
      ...group.formItems
    ].filter(item => !!item.developmentEvaluated).length;
  }

  getTotalCount(
    group: PediatricDevelopmentGroup
  ): number {
    return group.historyItems.length +
      group.formItems.length;
  }

  getItemColorIndex(
    item: ConsultationDevelopmentChildItem
  ): number {
    return Number(this.old) >=
      Number(item.setting?.minOld ?? 0) ? 1 : 0;
  }

  getRowClass(
    item: ConsultationDevelopmentChildItem
  ): string {
    const classes: string[] = [];

    if (item.developmentEvaluated) {
      classes.push('row-done');
    } else {
      classes.push(
        this.getItemColorIndex(item) === 1
          ? 'row-due'
          : 'row-upcoming'
      );
    }

    if (this.isItemDisabled(item)) {
      classes.push('row-disabled');
    }

    return classes.join(' ');
  }

  getDisabledReason(
    item: ConsultationDevelopmentChildItem
  ): string {
    if (!this.belongsToCurrentConsultation(item)) {
      return 'Saisi lors d’une consultation précédente';
    }

    if (this.isItemLocked(item)) {
      return 'Âge du repère dépassé';
    }

    return '';
  }

  getOldFormat(
    pediatricChild: PediatricChild
  ): { years: number; months: number } {
    return {
      months: pediatricChild.minOld % 12,
      years: Math.floor(pediatricChild.minOld / 12)
    };
  }

  trackByGroup(
    _: number,
    group: PediatricDevelopmentGroup
  ): number {
    return group.minOld;
  }

  trackByItem(
    _: number,
    item: ConsultationDevelopmentChildItem
  ): any {
    return item.setting?.id ?? this.getItemId(item);
  }
}